import React, { useMemo, useRef } from 'react';
import styled from 'styled-components';
import { CONTENT_WIDE, media } from '../../utils/media';
import theme from '../theme';
import Typo from '../Typo';
import Button from '../Button';
import IconAsButton from '../IconAsButton';
import SimpleButton from '../molecules/Button/SimpleButton';
import { useTranslation } from 'react-i18next';
import { getUserThemeColor } from '../theme/colors';

const StoreLogoWrapper = styled.div`
  position: absolute;
  top: 0;
  left: 50%;
  transform: translateX(-50%);
  height: 100%;
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1201;
  cursor: pointer;
  max-width: 300px;
`;
const Headerwrapper = styled.header`
  max-height: ${(props) => props.theme.headerSize};
  height: 100%;
  position: fixed;
  width: 100%;
  top: 0;
  left: 0;
  background-color: ${(props) => props.$background || props.theme.colors.NEU_0};
  box-shadow: ${({ theme, $inStore, $courseTheme }) => ($inStore ? theme.shadows.BOX_SHADOW : `0px 10px ${$courseTheme || theme.colors.PRIMARY}`)};
  z-index: 1199;
`;
const HeaderBar = styled.div`
  position: absolute;
  top: 0;
  width: 100%;
  max-width: ${CONTENT_WIDE}px;
  margin-left: auto;
  margin-right: auto;
  left: 0;
  right: 0;
  text-align: center;
  padding: 1rem 0.5rem;
  display: flex;
  justify-content: space-between;
  align-items: center;
  height: 100%;

  ${media.lessThan('desktop')} {
    padding: 1rem;
  }

  ${media.lessThan('mobile')} {
    padding: 0.5rem;
  }
`;

const LogoImage = styled.img.attrs((props) => ({
  src: props.src,
}))`
  max-height: 55px;
  max-width: 200px;
  object-fit: contain;

  ${media.greaterThan('tablet')} {
    max-width: 290px;
  }
  ${media.lessThan('mobile')} {
    max-width: 132px;
  }
`;

const Infowrapper = styled.div`
  margin-left: ${({ $smallerMargin }) => ($smallerMargin ? '45px' : '55px')};
`;

const FlexStyled = styled.div`
  z-index: 200;
  display: flex;
`;

const Header = ({
  cartItems,
  openCart,
  headerLogo,
  goToHome,
  ownerName,
  loggedIn,
  openLoginModal,
  openTocMenu,
  openEditLeftPanel,
  openCourseSettings,
  openLayoutOrganizer,
  inStore,
  inCourseView,
  inCourseEdit,
  bgColor,
  courseThemeColor,
  title,
  onToggleEditView,
}) => {
  const { t } = useTranslation();

  const logoRef = useRef(null);
  const elemWidth = useMemo(() => {
    return logoRef?.current?.offsetWidth;
  }, [logoRef?.current?.offsetWidth]); // eslint-disable-line react-hooks/exhaustive-deps

  const courseColor = useMemo(() => courseThemeColor && getUserThemeColor(courseThemeColor), [courseThemeColor]);

  return (
    <Headerwrapper $background={bgColor} $inStore={inStore} $courseTheme={courseColor}>
      {inStore && (
        <StoreLogoWrapper $elemWidth={elemWidth} ref={logoRef}>
          {headerLogo ? (
            <LogoImage src={headerLogo} onClick={goToHome} />
          ) : (
            <Button $asLink onClick={goToHome}>
              {ownerName}
            </Button>
          )}
        </StoreLogoWrapper>
      )}
      {inCourseView && headerLogo && (
        <StoreLogoWrapper $elemWidth={elemWidth} ref={logoRef}>
          <LogoImage src={headerLogo} />
        </StoreLogoWrapper>
      )}
      <HeaderBar>
        <FlexStyled>
          {loggedIn ? (
            <Infowrapper $smallerMargin={inCourseView || inCourseEdit}>
              {/* <Avatar url={userInfo?.avatarUrl} name={userInfo.displayName} onClick={openUserMenu} size='small' /> */}
              <Typo.TextTitle>{title}</Typo.TextTitle>
            </Infowrapper>
          ) : (
            <Button icon='Person' $asLink onClick={openLoginModal}>
              {t('auth.login')}
            </Button>
          )}
          {loggedIn && inCourseView && <IconAsButton iconName='List' color={courseColor} onlyMobile={true} clickHandle={openTocMenu} />}
          {loggedIn && inCourseEdit && (
            <>
              <IconAsButton iconName='List' color={courseColor} onlyMobile={true} clickHandle={openEditLeftPanel} />
              <IconAsButton iconName='Settings' color={courseColor} onlyMobile={true} clickHandle={openCourseSettings} />
              <IconAsButton iconName='EditLayout' color={courseColor} onlyMobile={true} clickHandle={openLayoutOrganizer} />
            </>
          )}
        </FlexStyled>
        {onToggleEditView && (inCourseView || inCourseEdit) ? (
          <SimpleButton
            startIcon={inCourseEdit ? 'Visible' : 'Edit'}
            variant='ghost'
            size='small'
            square
            onClick={onToggleEditView}
          >
            {inCourseEdit ? t('course.viewCourse') : t('course.editCourse')}
          </SimpleButton>
        ) : !!(cartItems || inStore) && (
          <IconAsButton
            hasBadge={!!cartItems}
            tooltipLabel={t('globals.openCart')}
            badgeInfo={cartItems}
            iconName='Cart'
            variant='primary'
            color={theme.colors.NEU_PRIMARY}
            clickHandle={openCart}
          />
        )}
      </HeaderBar>
    </Headerwrapper>
  );
};

export default Header;
